import { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { useForm } from "react-hook-form";
import TextField from "../Form/TextField";
import { useTranslationApp } from "../../lib/i18next";
import { NAME_PLACEHOLDER, FIELD_REQUIRED } from "../../i18n/keysTranslations";

const EditableText = ({
  value,
  sx,
  sxText = {},
  onCallbackSave = () => { },
  variantText = "body1",
  disabledEditable = false,
}) => {
  const [originalValue, setOriginalValue] = useState(value); // Estado para guardar el valor original
  const {
    control,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      text: value,
    },
  });
  const [isEditing, setIsEditing] = useState(false);
  const textFieldRef = useRef(null); // Reference to the text field container for click outside detection

  const { t } = useTranslationApp();
  const text = watch("text");

  useEffect(() => {
    setOriginalValue(value);
  }, [value]);

  // Save changes if text has been modified and exit edit mode
  const onSave = () => {
    if (text !== value) {
      if (!text || text.trim() === "") {
        // If text is empty, restore original value
        setValue("text", originalValue);
        onCallbackSave(originalValue);
      } else {
        onCallbackSave(text);
      }
    }
    setIsEditing(false);
  };

  // Handle keyboard events for saving (Enter) and canceling (Escape)
  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      onSave();
    } else if (e.key === "Escape") {
      setValue("text", value);
      setIsEditing(false);
    }
  };

  useEffect(() => {
    setValue("text", value);
    // eslint-disable-next-line
  }, [value]);

  // Handle clicks outside the text field
  useEffect(() => {
    const onClickOutside = (event) => {
      // Check if click was outside the text field container
      if (
        textFieldRef.current &&
        !textFieldRef.current.contains(event.target)
      ) {
        onSave();
      }
    };

    if (isEditing) {
      document.addEventListener("mousedown", onClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", onClickOutside);
    };
    // eslint-disable-next-line
  }, [isEditing, text, value]);

  return (
    <Box
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        ...sx,
        display: "flex",
        alignItems: "center",
      }}
    >
      {isEditing ? (
        // Container div for click outside detection
        <div ref={textFieldRef}>
          <TextField
            control={control}
            name={"text"}
            fullWidth
            placeholder={t(NAME_PLACEHOLDER)}
            variant="standard"
            rules={{
              required: { value: true, message: t(FIELD_REQUIRED) },
            }}
            errors={errors}
            onKeyDown={onKeyDown}
            autoFocus
            sx={{
              width: 300,
              "& .MuiInputBase-root": {
                fontSize: 20,
              },
            }}
          />
        </div>
      ) : (
        <Tooltip title={text}>
          <Typography
            variant={variantText}
            sx={{
              ...sxText,
              cursor: disabledEditable ? "default" : "pointer",
            }}
            onClick={() => !disabledEditable && setIsEditing(true)}
          >
            {text}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

export default EditableText;
